import { useMutation, useQueryClient } from '@tanstack/react-query';
import findIndex from 'lodash-es/findIndex';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import {
  Evaluation,
  Note,
} from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { cdnKeys } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/keys';
import { replaceItemAtIndex } from '@teetsh/app/src/utils/listMethods';
import { useTranslation } from 'react-i18next';
import { useEvaluationHistory } from './evaluationHistoryAtoms';

function updateNote({
  schoolyearId,
  schoolId,
  cdnId,
  evaluationId,
  note,
}: {
  schoolyearId: string;
  schoolId: string;
  cdnId: string;
  evaluationId: string;
  note: Note;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${cdnId}/evaluations/${evaluationId}/notes`;
  return request(requestURL, {
    method: 'POST',
    body: note,
  });
}

function replaceOrAddNote(notes: Note[], note: Note) {
  const index = findIndex(notes, n => n.id === note.id);
  return index < 0 ? [...notes, note] : replaceItemAtIndex(notes, note, index);
}

interface CarnetContext {
  id: string;
  minValue: number;
  maxValue: number;
}

export function useUpdateNote(
  // Ici plutôt que passer systématiquement le carnet context
  // on aurait pu utiliser useCDNContext();
  // je m'abstiens car dans un futur proche on permettra un system de notation
  // par eval, il faudra donc mettre d'ici le system de not de l'eval et pas
  // du carnet
  carnet: CarnetContext,
  undoRedoHistoryActions?: {
    addToPast: (ev: Evaluation) => void;
    resetFuture: () => void;
  },
) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['carnet']);
  const queryClient = useQueryClient();
  const enableUndoRedo =
    undoRedoHistoryActions?.addToPast && undoRedoHistoryActions?.resetFuture;

  const { mutateAsync } = useMutation({
    mutationFn: (note: Note) =>
      updateNote({
        schoolId: school.id,
        schoolyearId: schoolyear.id,
        cdnId: carnet.id,
        evaluationId: note.evaluationId,
        note: {
          ...note,
          notationSystemMin: carnet.minValue ?? 0,
          notationSystemMax: carnet.maxValue ?? 100,
        },
      }),
    onMutate: n => {
      const note = {
        ...n,
        notationSystemMin: carnet.minValue ?? 0,
        notationSystemMax: carnet.maxValue ?? 100,
      };
      if (enableUndoRedo) {
        // Get current evaluation state to add to history
        const currentEvaluation = queryClient.getQueryData<Evaluation>(
          cdnKeys.cdnEvaluationDetails(
            schoolyear.id,
            school.id,
            carnet.id,
            note.evaluationId,
          ),
        );

        if (currentEvaluation) {
          undoRedoHistoryActions?.addToPast(currentEvaluation);
        }
        undoRedoHistoryActions?.resetFuture();
      }

      // Update the evaluation in the query cache
      queryClient.setQueryData<Evaluation | undefined>(
        cdnKeys.cdnEvaluationDetails(
          schoolyear.id,
          school.id,
          carnet.id,
          note.evaluationId,
        ),
        ev => {
          if (!ev) {
            return ev;
          }

          return {
            ...ev,
            notes: replaceOrAddNote(ev.notes || [], note),
          };
        },
      );

      // Update the evaluations list in the query cache
      queryClient.setQueryData<Evaluation[]>(
        cdnKeys.cdnEvaluations(schoolyear.id, school.id, carnet.id),
        evals =>
          (evals || []).map(ev => {
            if (ev.id === note.evaluationId) {
              return {
                ...ev,
                notes: replaceOrAddNote(ev.notes || [], note),
              };
            }
            return ev;
          }),
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluations(schoolyear.id, school.id, carnet.id),
      });
      notifyError({
        title: t('carnet:messages.evaluation.updateNoteError'),
      });
    },
  });
  return mutateAsync;
}

export function useUpdateNoteWithHistory(
  carnetContext: CarnetContext,
  evaluationId: string,
) {
  const { addToPast, resetFuture } = useEvaluationHistory(evaluationId);
  return useUpdateNote(carnetContext, {
    addToPast,
    resetFuture,
  });
}
